import type { ProcessingOptions } from '../types/resize';

function drawImageWithEffects(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  options: ProcessingOptions
) {
  const adj = options.adjustments;
  const rotation = adj?.rotation || 0;
  const flipH = adj?.flipH || false;
  const flipV = adj?.flipV || false;

  ctx.save();

  // Apply CSS Filters
  const filters: string[] = [];
  if (adj) {
    if (adj.brightness !== 100) filters.push(`brightness(${adj.brightness}%)`);
    if (adj.contrast !== 100) filters.push(`contrast(${adj.contrast}%)`);
    if (adj.saturation !== 100) filters.push(`saturate(${adj.saturation}%)`);
    if (adj.blur > 0) filters.push(`blur(${adj.blur}px)`);
    if (adj.grayscale) filters.push('grayscale(100%)');
    if (adj.sepia) filters.push('sepia(100%)');
  }

  if (filters.length > 0) {
    ctx.filter = filters.join(' ');
  }

  // Handle Rotation & Flipping
  const destW = rotation % 180 !== 0 ? targetHeight : targetWidth;
  const destH = rotation % 180 !== 0 ? targetWidth : targetHeight;

  ctx.translate(destW / 2, destH / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

  // Calculate original image aspect ratio vs target canvas aspect ratio
  const fitMode = options.resize?.fitMode || (options.resize?.maintainAspectRatio ? 'fit' : 'crop');
  const imgRatio = img.width / img.height;
  const targetRatio = targetWidth / targetHeight;
  const isRatioMismatch = Math.abs(imgRatio - targetRatio) > 0.01;

  // Default to Smart Center-Crop whenever aspect ratios differ to prevent ANY pixel stretching/squishing!
  const shouldCrop = fitMode === 'crop' || (isRatioMismatch && fitMode !== 'stretch');

  if (shouldCrop) {
    let srcX = 0;
    let srcY = 0;
    let srcW = img.width;
    let srcH = img.height;

    if (imgRatio > targetRatio) {
      // Image is wider than target preset -> Crop horizontal edges evenly from center
      srcW = img.height * targetRatio;
      srcX = (img.width - srcW) / 2;
    } else {
      // Image is taller than target preset -> Crop vertical edges evenly from center
      srcH = img.width / targetRatio;
      srcY = (img.height - srcH) / 2;
    }

    ctx.drawImage(
      img,
      srcX,
      srcY,
      srcW,
      srcH,
      -targetWidth / 2,
      -targetHeight / 2,
      targetWidth,
      targetHeight
    );
  } else {
    ctx.drawImage(img, -targetWidth / 2, -targetHeight / 2, targetWidth, targetHeight);
  }
  ctx.restore();

  // Draw Watermark Overlay
  const wm = options.watermark;
  if (wm?.enabled && wm.text.trim()) {
    ctx.save();
    const fontSize = wm.fontSize || 32;
    ctx.font = `bold ${fontSize}px sans-serif`;
    ctx.fillStyle = wm.color || '#ffffff';
    ctx.globalAlpha = (wm.opacity || 70) / 100;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 6;

    const metrics = ctx.measureText(wm.text);
    const textWidth = metrics.width;
    const padding = 20;

    let x = padding;
    let y = fontSize + padding;

    if (wm.position === 'top-right') {
      x = destW - textWidth - padding;
      y = fontSize + padding;
    } else if (wm.position === 'center') {
      x = (destW - textWidth) / 2;
      y = (destH + fontSize / 2) / 2;
    } else if (wm.position === 'bottom-left') {
      x = padding;
      y = destH - padding;
    } else if (wm.position === 'bottom-right') {
      x = destW - textWidth - padding;
      y = destH - padding;
    }

    ctx.fillText(wm.text, x, y);
    ctx.restore();
  }
}

export async function processImage(
  file: File,
  options: ProcessingOptions
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = async () => {
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      try {
        let targetWidth = options.resize?.width || img.width;
        let targetHeight = options.resize?.height || img.height;
        let quality = 0.9;
        let dataUrl: string;

        const rotation = options.adjustments?.rotation || 0;
        const isSwapped = rotation % 180 !== 0;

        // Size-based optimization
        if (options.output.targetSize) {
          const targetBytes = options.output.targetSize * 1024;
          const aspectRatio = img.width / img.height;

          targetWidth = img.width;
          targetHeight = img.height;

          let iterations = 0;
          const maxIterations = 25;

          while (iterations < maxIterations) {
            iterations++;
            canvas.width = isSwapped ? targetHeight : targetWidth;
            canvas.height = isSwapped ? targetWidth : targetHeight;

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            drawImageWithEffects(ctx, img, targetWidth, targetHeight, options);

            dataUrl = canvas.toDataURL(options.output.format, quality);

            const base64Data = dataUrl.split(',')[1] || '';
            const currentSize = Math.round(base64Data.length * 0.75);
            const sizeDiff = currentSize - targetBytes;

            if (Math.abs(sizeDiff) / targetBytes < 0.1 || (currentSize <= targetBytes && iterations > 5)) {
              break;
            }

            const scaleFactor = Math.sqrt(targetBytes / Math.max(currentSize, 1));
            targetWidth = Math.max(20, Math.round(targetWidth * scaleFactor));
            targetHeight = Math.max(20, Math.round(targetWidth / aspectRatio));

            if (targetWidth < 50 || targetHeight < 50) {
              quality = Math.max(0.1, quality - 0.1);
              if (quality <= 0.1) break;
              targetWidth = Math.round(img.width * 0.5);
              targetHeight = Math.round(targetWidth / aspectRatio);
            }
          }
        } else {
          // Dimension-based processing
          canvas.width = isSwapped ? targetHeight : targetWidth;
          canvas.height = isSwapped ? targetWidth : targetHeight;

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          drawImageWithEffects(ctx, img, targetWidth, targetHeight, options);

          quality = (options.output.quality || 90) / 100;
          dataUrl = canvas.toDataURL(options.output.format, quality);
        }

        URL.revokeObjectURL(img.src);
        resolve(dataUrl);
      } catch (error) {
        URL.revokeObjectURL(img.src);
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
}